/**
 * What is plugged in, and what it is called.
 *
 * Two things the pickers need that nothing else in the app knows: the list
 * itself, and whether the browser is willing to NAME the entries in it.
 * `enumerateDevices` answers before the microphone has ever been granted, but
 * with every `label` blank - so an operator arriving at this panel for the first
 * time would be choosing between four indistinguishable blanks. The names arrive
 * with the permission, which the level meter next door asks for, and `refresh`
 * is what brings them in once it has.
 *
 * The list is also live. A headset is plugged in mid-morning and unplugged at
 * lunch, and `devicechange` is the browser saying so - without it the panel
 * would be a snapshot of whatever was attached when the call window opened.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { selectableDevices } from "./choice";
import { canRouteAudio } from "./sink";

export interface AudioDeviceList {
  /** Microphones. */
  inputs: MediaDeviceInfo[];
  /** Speakers. Empty in browsers that do not enumerate outputs at all. */
  outputs: MediaDeviceInfo[];
  /** Every id above, for `effectiveAudioChoice`. */
  attached: ReadonlySet<string>;
  /** The entries carry real names, which is the same thing as "mic granted". */
  named: boolean;
  /**
   * The browser has answered once, whatever it answered.
   *
   * Not the same question as "is the list non-empty": a refusal and a machine
   * with nothing plugged into it both end here with no devices, and both are
   * settled answers. What reads this is the meter, which must not open a
   * capture before it knows whether the chosen microphone is attached - with an
   * empty list `effectiveAudioChoice` drops every id to "", so a capture opened
   * a tick too early grabs the system default and is then torn down and opened
   * again on the real device the moment the list lands. Two device opens back
   * to back, both on the main thread, both inside the dropdown's entrance.
   */
  ready: boolean;
  /** This browser enumerates devices at all. Needs a secure context. */
  supported: boolean;
  /** Sound can be sent somewhere other than the system default. */
  routable: boolean;
  /** Ask the browser again - after a permission was granted, say. */
  refresh: () => void;
}

const EMPTY: MediaDeviceInfo[] = [];

export function useAudioDevices(): AudioDeviceList {
  const supported =
    typeof navigator !== "undefined" && typeof navigator.mediaDevices?.enumerateDevices === "function";
  const [devices, setDevices] = useState<MediaDeviceInfo[]>(EMPTY);
  // A browser that cannot enumerate at all has already given its answer, and
  // the panel says so in one line instead of waiting on a list that never comes.
  const [ready, setReady] = useState(!supported);
  /** Bumped by `refresh`, which is what re-runs the effect below. */
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!supported) return;
    let alive = true;

    const read = () => {
      navigator.mediaDevices
        .enumerateDevices()
        .then((all) => {
          if (alive) setDevices(all);
        })
        .catch(() => {
          // Refused, or no such API behind a policy. An empty list draws as
          // "System default only", which is exactly what is on offer then.
        })
        .finally(() => {
          // Settled either way. A refusal is an answer too, and anything
          // waiting on this must not be left waiting by one.
          if (alive) setReady(true);
        });
    };

    read();
    navigator.mediaDevices.addEventListener?.("devicechange", read);
    return () => {
      alive = false;
      navigator.mediaDevices.removeEventListener?.("devicechange", read);
    };
  }, [supported, nonce]);

  return useMemo(() => {
    const inputs = selectableDevices(devices.filter((d) => d.kind === "audioinput"));
    const outputs = selectableDevices(devices.filter((d) => d.kind === "audiooutput"));
    const attached = new Set([...inputs, ...outputs].map((d) => d.deviceId));
    return {
      inputs,
      outputs,
      attached,
      // One named entry is enough: Chrome hands back a labelled list or a blank
      // one, never a mixture, and asking for ALL of them to be named would go
      // false for a single virtual device the driver left anonymous.
      named: [...inputs, ...outputs].some((d) => d.label.trim() !== ""),
      ready,
      supported,
      routable: canRouteAudio(),
      refresh,
    };
  }, [devices, ready, supported, refresh]);
}
